import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { getAwsKey } from '../../store/actions/piece-video/piece-video.action';
import { toast } from 'react-toastify';
import GPassword from 'generate-password';

// Validation schema
const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  email: Yup.string().email('Invalid email address').required('Email is required'),
  mobile: Yup.string()
    .matches(/^[0-9+\-\s()]+$/, 'Invalid mobile number')
    .required('Mobile is required'),
  password: Yup.string().when('mode', {
    is: 'CREATE',
    then: (schema) => schema.required('Password is required').min(6, 'Password must be at least 6 characters'),
    otherwise: (schema) => schema.when('forgotClick', {
      is: true,
      then: (schema) => schema.required('Password is required').min(6, 'Password must be at least 6 characters'),
      otherwise: (schema) => schema
    })
  }),
  retypePassword: Yup.string().when(['mode', 'forgotClick'], {
    is: (mode, forgotClick) => mode === 'CREATE' || forgotClick,
    then: (schema) => schema.oneOf([Yup.ref('password'), null], 'Passwords must match').required('Please confirm password'),
    otherwise: (schema) => schema
  }),
});

// Custom input component - Bootstrap 5 compatible
const CustomInput = ({ field, form, parentClass, ...props }) => {
  const hasError = form.touched[field.name] && form.errors[field.name];

  return (
    <div className={parentClass || 'mb-3'}>
      <input
        {...field}
        {...props}
        className={`${props.className || 'form-control'} ${hasError ? 'is-invalid' : ''}`}
        disabled={props.disabled}
      />
      {hasError && (
        <div className="invalid-feedback">
          {form.errors[field.name]}
        </div>
      )}
    </div>
  );
};

const CreateAdmin = ({ actions, categories, isCreating }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [state, setState] = useState({
    mode: 'CREATE',
    forgotClick: false,
    assigned_categories: [],
    systemPassword: null,
    adminData: null,
    isLoading: false,
  });

  // Memoize the initial values to prevent unnecessary re-renders
  const initialValues = useMemo(() => {
    if (state.mode === 'EDIT' && state.adminData) {
      return {
        name: state.adminData.name || '',
        email: state.adminData.email || '',
        mobile: state.adminData.mobile || '',
        password: '',
        retypePassword: '',
        mode: state.mode,
        forgotClick: state.forgotClick,
      };
    }
    return {
      name: '',
      email: '',
      mobile: '',
      password: '',
      retypePassword: '',
      mode: state.mode,
      forgotClick: state.forgotClick,
    };
  }, [state.mode, state.adminData, state.forgotClick]);

  // Memoize the loadAdminData function
  const loadAdminData = useCallback(async (adminId) => {
    if (state.isLoading) return; // Prevent multiple simultaneous calls

    setState(prev => ({ ...prev, isLoading: true }));
    try {
      let data = await actions.getAdminByID(adminId);
      if (data && data.assigned_categories) {
        data.assigned_categories = data.assigned_categories.split(',');
        setState(prev => ({
          ...prev,
          assigned_categories: data.assigned_categories,
          adminData: data,
          isLoading: false,
        }));
      }
    } catch (error) {
      toast.error('Failed to load admin data');
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, [actions.getAdminByID, state.isLoading]);

  // Fix useEffect dependencies
  useEffect(() => {
    const mode = id ? 'EDIT' : 'CREATE';

    // Only call getCategories once
    if (categories.length === 0) {
      actions.getCategories();
    }

    if (mode === 'EDIT' && !state.adminData && !state.isLoading) {
      setState(prev => ({ ...prev, mode: 'EDIT' }));
      loadAdminData(id);
    } else if (mode === 'CREATE' && state.mode !== 'CREATE') {
      setState(prev => ({ ...prev, mode: 'CREATE' }));
    }
  }, [id]); // Remove actions from dependencies

  const handleSubmit = useCallback(async (values, { setSubmitting }) => {
    try {
      let payload = {
        email: values.email,
        mobile: values.mobile,
        name: values.name,
        role: 'subadmin',
        assigned_categories: state.assigned_categories.join(','),
      };

      if (state.mode === 'EDIT') {
        payload = {
          ...payload,
          id: state.adminData.id,
          user_id: state.adminData.id,
        };

        if (state.forgotClick) {
          payload = {
            ...payload,
            password: values.password,
            password1: values.retypePassword,
          };
        }

        await actions.editAdmin(payload);
        toast.success('Admin updated successfully');
      } else {
        payload = {
          ...payload,
          password: values.password,
          password1: values.retypePassword,
        };

        await actions.createAdmin(payload);
        toast.success('Admin created successfully');
      }
    } catch (error) {
      toast.error(state.mode === 'EDIT' ? 'Failed to update admin' : 'Failed to create admin');
    } finally {
      setSubmitting(false);
    }
  }, [state.mode, state.adminData, state.assigned_categories, state.forgotClick, actions]);

  const toggleCategory = useCallback((id) => {
    setState(prev => {
      let { assigned_categories } = prev;
      let add = true;

      if (assigned_categories.includes(id + '|edit') || assigned_categories.includes(id + '|view')) {
        add = false;
      }
      assigned_categories = assigned_categories.filter((d) => d !== id + '|edit' && d !== id + '|view');

      if (add) {
        assigned_categories.push(id + '|view');
      }

      return { ...prev, assigned_categories: [...assigned_categories] };
    });
  }, []);

  const generatePassword = useCallback((setFieldValue) => {
    let systemPassword = GPassword.generate({ length: 10, numbers: true });
    setState(prev => ({ ...prev, systemPassword }));
    setFieldValue('password', systemPassword);
    setFieldValue('retypePassword', systemPassword);
  }, []);

  const alterAccess = useCallback((id, access) => {
    setState(prev => {
      let { assigned_categories } = prev;
      if (assigned_categories.includes(id + '|edit') || assigned_categories.includes(id + '|view')) {
        assigned_categories = assigned_categories.filter((d) => d !== id + '|edit' && d !== id + '|view');
      }
      let value = id + '|' + access;
      assigned_categories.push(value);

      return { ...prev, assigned_categories: [...assigned_categories] };
    });
  }, []);

  const renderCategories = useCallback(() => {
    return categories.map(({ name, id }, ind) => {
      let edit = state.assigned_categories.includes(id + '|edit');
      let view = state.assigned_categories.includes(id + '|view');
      let v = 'view';

      if (edit) {
        v = 'edit';
      }

      return (
        <tr key={id}> {/* Use id instead of index for better React performance */}
          <td>
            <div className="form-check">
              <input
                type="checkbox"
                checked={edit || view}
                id={`category-${id}`}
                value={id}
                className="form-check-input"
                onChange={() => toggleCategory(id)}
              />
              <label className="form-check-label" htmlFor={`category-${id}`}>
                {name}
              </label>
            </div>
          </td>
          <td>
            <select
              disabled={!(edit || view)}
              value={v}
              className="form-select"
              onChange={(e) => {
                alterAccess(id, e.target.value);
              }}
            >
              <option value="view">View</option>
              <option value="edit">Edit</option>
            </select>
          </td>
        </tr>
      );
    });
  }, [categories, state.assigned_categories, toggleCategory, alterAccess]);

  const { mode, forgotClick, systemPassword } = state;

  // Show loading if data is being fetched
  if (mode === 'EDIT' && !state.adminData && state.isLoading) {
    return (
      <div className="d-flex justify-content-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <section className="well container">
      <div className="">
        <div className="row">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={false} // Only reinitialize when necessary
            key={`${mode}-${state.adminData?.id || 'new'}`} // Force remount when switching modes
          >
            {({ isSubmitting, values, setFieldValue, errors, touched }) => (
              <Form>
                {/* Name Field */}
                <div className="row mb-3">
                  <div className="col-md-3">
                    <label className="form-label">Name</label>
                  </div>
                  <div className="col-md-9">
                    <Field
                      name="name"
                      type="text"
                      placeholder="Name"
                      component={CustomInput}
                      className="form-control"
                      parentClass="mb-0"
                    />
                  </div>
                </div>

                {/* Email Field */}
                <div className="row mb-3">
                  <div className="col-md-3">
                    <label className="col-form-label">Email</label>
                  </div>
                  <div className="col-md-9">
                    <Field
                      name="email"
                      type="email"
                      placeholder="Email"
                      component={CustomInput}
                      className="form-control"
                      parentClass="mb-0"
                    />
                  </div>
                </div>

                {/* Mobile Field */}
                <div className="row mb-3">
                  <div className="col-md-3">
                    <label className="col-form-label">Mobile</label>
                  </div>
                  <div className="col-md-9">
                    <Field
                      name="mobile"
                      type="tel"
                      placeholder="Mobile"
                      maxLength="13"
                      component={CustomInput}
                      className="form-control"
                      parentClass="mb-0"
                    />
                  </div>
                </div>

                {/* Reset Password Toggle */}
                {mode === 'EDIT' && (
                  <div className="row mb-3">
                    <div className="col-md-12">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => {
                          setState(prev => ({ ...prev, forgotClick: !prev.forgotClick }));
                          if (!forgotClick) {
                            setFieldValue('password', '');
                            setFieldValue('retypePassword', '');
                          }
                        }}
                      >
                        {!forgotClick ? 'Reset Password' : 'Cancel Reset'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Password Fields */}
                {(mode === 'CREATE' || forgotClick) && (
                  <>
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <label className="col-form-label">Password</label>
                      </div>
                      <div className="col-md-9">
                        <Field
                          name="password"
                          type="password"
                          placeholder="Password"
                          component={CustomInput}
                          className="form-control"
                          parentClass="mb-0"
                        />
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-md-3">
                        <label className="col-form-label">Re-enter Password</label>
                      </div>
                      <div className="col-md-9">
                        <Field
                          name="retypePassword"
                          type="password"
                          placeholder="Re-enter Password"
                          component={CustomInput}
                          className="form-control"
                          parentClass="mb-0"
                        />
                      </div>
                    </div>

                    {/* System Password Generator */}
                    <div className="row mb-3">
                      <div className="col-md-3"></div>
                      <div className="col-md-9">
                        <button
                          type="button"
                          className="btn btn-info btn-sm mb-2"
                          onClick={() => generatePassword(setFieldValue)}
                        >
                          Generate System Password
                        </button>
                        {systemPassword && (
                          <div className="alert alert-warning mt-2">
                            <small className="text-muted">
                              System Password (shown only once, copy and save instantly):
                            </small>
                            <br />
                            <strong className="text-primary">{systemPassword}</strong>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Categories Table */}
                <div className="row mb-3">
                  <div className="col-md-3">
                    <label className="col-form-label">Accessible Categories</label>
                  </div>
                  <div className="col-md-9">
                    <div className="table-responsive">
                      <table className="table table-bordered table-hover">
                        <thead className="table-light">
                          <tr>
                            <th>Category</th>
                            <th>Access Type</th>
                          </tr>
                        </thead>
                        <tbody>
                          {renderCategories()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="row">
                  <div className="col-12">
                    <div className="d-grid">
                      <button
                        className="btn btn-bg w-100 "
                        type="submit"
                        disabled={isCreating || isSubmitting}
                      >
                        {isCreating || isSubmitting ? (
                          <>
                            <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                            Processing...
                          </>
                        ) : (
                          <span>{mode === 'CREATE' ? 'Create Admin' : 'Update Admin'}</span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </section>
  );
};

export default CreateAdmin;
